import { createContext, useContext, type ReactNode } from "react";
import { useMe } from "../api/hooks/useAuth";
import type { AuthUser } from "../api/types";

interface AuthContextValue {
  user: AuthUser | null | undefined; // undefined while loading
  isLoading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Plain Context for the auth session (per plan: react-query for server state,
// Context only for the session) — the session's source of truth is still
// useMe()'s react-query cache, this just avoids re-querying it in every page.
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useMe();
  const value: AuthContextValue = {
    user,
    isLoading,
    isAdmin: user?.role === "admin",
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}
